import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import PaymentsIcon from '@mui/icons-material/Payments';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { useAuth } from '../auth/AuthContext';
import { canAccessWaiter, canCancelSentSalonItem, canCloseSalonSession, canManageSalonTables } from '../utils/auth';
import {
  getSalonConsumptionItems,
  getSalonPaymentRequest,
  getSalonUndeliveredItems,
  isSalonPaymentAwaitingCashier,
  salonPaymentMethodLabel,
  sortSalonTablesForCashier,
} from '../utils/salonOperation';
import { keyframes } from '@mui/material/styles';
import TableQrDialog from './TableQrDialog';
import { printSalonConference } from '../utils/salonConferencePrint';
import { getTenantSettingsSession, getTenantStorageKey, PRINTER_SETTINGS_KEY } from '../utils/tenantSettingsStorage';

const API_URL = getApiUrl();

const paymentBlink = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.22); transform: translateY(0); }
  50% { box-shadow: 0 0 0 8px rgba(124, 58, 237, 0.34); transform: translateY(-2px); }
`;

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUS_LABELS = {
  livre: 'Livre',
  ocupada: 'Ocupada',
  aguardando_preparo: 'Aguardando preparo',
  consumindo: 'Consumindo',
  aguardando_pagamento: 'Aguardando pagamento',
  fechada: 'Fechada',
  inativa: 'Inativa',
};

const STATUS_COLORS = {
  livre: { border: '#16a34a', bg: '#f0fdf4', color: '#166534' },
  ocupada: { border: '#f59e0b', bg: '#fffbeb', color: '#92400e' },
  aguardando_preparo: { border: '#ef4444', bg: '#fef2f2', color: '#991b1b' },
  consumindo: { border: '#2563eb', bg: '#eff6ff', color: '#1d4ed8' },
  aguardando_pagamento: { border: '#7c3aed', bg: '#f5f3ff', color: '#5b21b6' },
  fechada: { border: '#64748b', bg: '#f8fafc', color: '#334155' },
  inativa: { border: '#94a3b8', bg: '#f1f5f9', color: '#64748b' },
};

const normalizeMoney = (value) => {
  const numeric = Number(String(value || '').replace(',', '.').replace(/[^\d.]/g, '') || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const paymentLabel = {
  pix: 'PIX',
  debit: 'Débito',
  credit: 'Crédito',
  cash: 'Dinheiro',
};

const buildPayments = (draft, totalDue) => {
  const rows = [
    { key: 'pix', method: 'PIX', amount: normalizeMoney(draft.pix) },
    { key: 'debit', method: 'DEBIT_CARD', amount: normalizeMoney(draft.debit) },
    { key: 'credit', method: 'CREDIT_CARD', amount: normalizeMoney(draft.credit) },
    { key: 'cash', method: 'CASH', amount: normalizeMoney(draft.cash) },
  ].filter((item) => item.amount > 0);
  const paid = rows.reduce((sum, item) => sum + item.amount, 0);
  const nonCash = rows.filter((item) => item.method !== 'CASH').reduce((sum, item) => sum + item.amount, 0);
  const cash = rows.find((item) => item.method === 'CASH');
  if (cash) cash.change = Math.max(0, paid - totalDue);
  return { rows, paid, nonCash };
};

const calculateCloseTotals = (session, draft) => {
  const subtotal = Number(session?.subtotal ?? session?.totalConsumed ?? session?.total ?? 0);
  const discount = normalizeMoney(draft.discount);
  const serviceFee = draft.applyServiceFee === undefined
    ? normalizeMoney(draft.serviceFee)
    : draft.applyServiceFee ? roundMoney(subtotal * 0.1) : 0;
  const totalDue = Math.max(0, subtotal - discount + serviceFee);
  const payments = buildPayments(draft, totalDue);
  return {
    subtotal,
    discount,
    serviceFee,
    totalDue,
    paid: payments.paid,
    nonCash: payments.nonCash,
    remaining: Math.max(0, totalDue - payments.paid),
    change: Math.max(0, payments.paid - totalDue),
    payments,
  };
};

const groupItemsByRound = (items = []) => {
  const groups = {};
  items.forEach((item) => {
    const key = item.roundNumber ? `Rodada ${item.roundNumber}` : 'Ainda não enviados';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.entries(groups);
};

const SalonManagementPanel = ({ managementActionContainer = null }) => {
  const auth = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [search, setSearch] = useState('');
  const [managementOpen, setManagementOpen] = useState(false);
  const [operationSettings, setOperationSettings] = useState(null);
  const [operationSettingsError, setOperationSettingsError] = useState('');
  const [operationSettingsSaving, setOperationSettingsSaving] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [tableDialog, setTableDialog] = useState({ open: false, table: null });
  const [tableForm, setTableForm] = useState({ number: '', name: '', printPointIds: ['caixa'] });
  const [tableFormError, setTableFormError] = useState('');
  const [printPoints, setPrintPoints] = useState([]);
  const [printPointsLoading, setPrintPointsLoading] = useState(false);
  const [printPointsError, setPrintPointsError] = useState('');
  const [openingTable, setOpeningTable] = useState(null);
  const [openForm, setOpenForm] = useState({ customerName: '', peopleCount: '' });
  const [closeDialog, setCloseDialog] = useState({ open: false, session: null });
  const [paymentDraft, setPaymentDraft] = useState({ discount: '', applyServiceFee: true, pix: '', debit: '', credit: '', cash: '' });
  const [closeError, setCloseError] = useState('');
  const [printingConference, setPrintingConference] = useState(false);
  const [paymentReviewDialog, setPaymentReviewDialog] = useState({ open: false, session: null });
  const [paymentReviewReason, setPaymentReviewReason] = useState('');
  const [paymentReviewError, setPaymentReviewError] = useState('');
  const [paymentReviewLoading, setPaymentReviewLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, item: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [qrTable, setQrTable] = useState(null);
  const mayManageTables = canManageSalonTables(auth.user);
  const mayConfigureOperation = ['programador', 'administrador', 'gerente'].includes(String(auth.user?.role || '').toLowerCase());
  const mayOperateTables = canAccessWaiter(auth.user);
  const mayClose = canCloseSalonSession(auth.user);
  const mayCancelSent = canCancelSentSalonItem(auth.user);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const closeTotals = useMemo(() => calculateCloseTotals(closeDialog.session, paymentDraft), [closeDialog.session, paymentDraft]);
  const prioritizedTables = useMemo(() => sortSalonTablesForCashier(tables), [tables]);
  const managementTables = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('pt-BR');
    const filtered = tables.filter((table) => {
      const matchesStatus = statusFilter === 'todos' || table.status === statusFilter;
      const searchableText = `${table.number || ''} ${table.name || ''}`.toLocaleLowerCase('pt-BR');
      return matchesStatus && (!normalizedSearch || searchableText.includes(normalizedSearch));
    });
    return sortSalonTablesForCashier(filtered);
  }, [search, statusFilter, tables]);
  const activePrintPoints = useMemo(() => (
    printPoints.filter((point) => point?.enabled !== false && String(point?.id || '').trim())
  ), [printPoints]);

  useEffect(() => {
    if (!managementOpen || !mayConfigureOperation) return undefined;
    let active = true;
    setOperationSettings(null);
    setOperationSettingsError('');
    apiFetch(`${API_URL}/api/salon/operation-settings`)
      .then(async response => ({ response, data: await response.json() }))
      .then(({ response, data }) => {
        if (!active) return;
        if (!response.ok || !data.settings) throw new Error(data.error || 'Não foi possível carregar a configuração de abertura.');
        setOperationSettings(data.settings);
      }).catch(error => { if (active) setOperationSettingsError(error.message); });
    return () => { active = false; };
  }, [managementOpen, mayConfigureOperation]);

  const saveOperationSettings = async () => {
    if (!operationSettings || operationSettingsSaving) return;
    setOperationSettingsSaving(true);
    setOperationSettingsError('');
    try {
      const response = await apiFetch(`${API_URL}/api/salon/operation-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(operationSettings),
      });
      const data = await response.json();
      if (!response.ok || !data.settings) throw new Error(data.error || 'Não foi possível salvar a configuração de abertura.');
      setOperationSettings(data.settings);
      setSuccess('Configuração de abertura salva.');
    } catch (error) { setOperationSettingsError(error.message); }
    finally { setOperationSettingsSaving(false); }
  };

  const loadPrintPoints = useCallback(async () => {
    if (!mayManageTables) return;
    setPrintPointsLoading(true);
    setPrintPointsError('');
    try {
      const response = await apiFetch(`${API_URL}/api/salon/print-points`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar os pontos de impressão.');
      setPrintPoints(Array.isArray(data.points) ? data.points : []);
    } catch (err) {
      setPrintPointsError(err.message || 'Não foi possível carregar os pontos de impressão.');
    } finally {
      setPrintPointsLoading(false);
    }
  }, [mayManageTables]);

  const loadTables = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const response = await apiFetch(`${API_URL}/api/salon/tables`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar mesas.');
      setTables(data.tables || []);
      if (selectedSession?.id) {
        const current = (data.tables || []).find((table) => Number(table.activeSession?.id) === Number(selectedSession.id));
        setSelectedSession(current?.activeSession || null);
      }
    } catch (err) {
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedSession?.id]);

  useEffect(() => {
    loadTables();
    const interval = window.setInterval(() => loadTables({ silent: true }), 10000);
    return () => window.clearInterval(interval);
  }, [loadTables]);

  useEffect(() => {
    const refresh = () => loadTables();
    window.addEventListener('brutos:salon-refresh', refresh);
    return () => window.removeEventListener('brutos:salon-refresh', refresh);
  }, [loadTables]);

  useEffect(() => {
    loadPrintPoints();
  }, [loadPrintPoints]);

  useEffect(() => {
    if (!tableDialog.open || !activePrintPoints.length) return;
    const availableIds = new Set(activePrintPoints.map((point) => String(point.id)));
    const defaultPointId = availableIds.has('caixa') ? 'caixa' : String(activePrintPoints[0]?.id || '');
    setTableForm((prev) => {
      const selected = (prev.printPointIds || []).map(String).filter((id) => availableIds.has(id)).slice(0, 3);
      return { ...prev, printPointIds: selected.length ? selected : [defaultPointId] };
    });
  }, [activePrintPoints, tableDialog.open]);

  const fetchSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    const response = await apiFetch(`${API_URL}/api/salon/sessions/${sessionId}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Falha ao carregar comanda.');
    return data.session;
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    setError('');
    try {
      const session = await fetchSession(sessionId);
      if (session) setSelectedSession(session);
    } catch (err) {
      setError(err.message || 'Não foi possível carregar a comanda.');
    }
  }, [fetchSession]);

  const openSessionDetails = useCallback((sessionId) => {
    loadSession(sessionId);
  }, [loadSession]);

  const handleOperationalTableClick = (table) => {
    if (!table.active || table.status === 'inativa') return;
    if (table.activeSession?.id) {
      openSessionDetails(table.activeSession.id);
      return;
    }
    if (mayOperateTables) setOpeningTable(table);
  };

  const openTableDialog = (table = null) => {
    const availableIds = new Set(activePrintPoints.map((point) => String(point.id)));
    const defaultPointId = availableIds.has('caixa') ? 'caixa' : String(activePrintPoints[0]?.id || '');
    const rawPointIds = Array.isArray(table?.printPointIds) ? table.printPointIds.map(String).slice(0, 3) : [];
    const selectedPointIds = activePrintPoints.length
      ? rawPointIds.filter((id) => availableIds.has(id))
      : rawPointIds;
    setTableForm({
      number: table?.number || '',
      name: table?.name || '',
      printPointIds: selectedPointIds.length ? selectedPointIds : [defaultPointId],
    });
    setTableFormError('');
    setTableDialog({ open: true, table });
  };

  const changeTablePrintPoint = (index, pointId) => {
    setTableForm((prev) => {
      const next = [...(prev.printPointIds || [''])];
      if (pointId && next.some((value, currentIndex) => currentIndex !== index && value === pointId)) return prev;
      next[index] = pointId;
      return { ...prev, printPointIds: next };
    });
  };

  const addTablePrintPoint = () => {
    setTableForm((prev) => {
      const current = Array.isArray(prev.printPointIds) ? prev.printPointIds : [''];
      if (current.length >= 3 || current.some((id) => !id)) return prev;
      return { ...prev, printPointIds: [...current, ''] };
    });
  };

  const removeTablePrintPoint = (index) => {
    setTableForm((prev) => {
      const current = Array.isArray(prev.printPointIds) ? prev.printPointIds : [''];
      const next = current.filter((_, currentIndex) => currentIndex !== index);
      return { ...prev, printPointIds: next.length ? next : [''] };
    });
  };

  const saveTable = async () => {
    setError('');
    setTableFormError('');
    const activeIds = new Set(activePrintPoints.map((point) => String(point.id)));
    const selectedPointIds = [...new Set((tableForm.printPointIds || []).filter((id) => activeIds.has(String(id))))].slice(0, 3);
    if (!selectedPointIds.length) {
      setTableFormError('Selecione pelo menos um ponto de impressão ativo para esta mesa.');
      return;
    }
    try {
      const url = tableDialog.table
        ? `${API_URL}/api/salon/tables/${tableDialog.table.id}`
        : `${API_URL}/api/salon/tables`;
      const response = await apiFetch(url, {
        method: tableDialog.table ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          number: tableForm.number,
          name: tableForm.name,
          printPointIds: selectedPointIds,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar mesa.');
      setTableDialog({ open: false, table: null });
      setSuccess('Mesa salva.');
      await loadTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleTableActive = async (table) => {
    setError('');
    try {
      const action = table.active ? 'inactivate' : 'activate';
      const response = await apiFetch(`${API_URL}/api/salon/tables/${table.id}/${action}`, { method: 'PATCH' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao alterar mesa.');
      setSuccess(table.active ? 'Mesa inativada.' : 'Mesa ativada.');
      await loadTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const openSession = async (table) => {
    setError('');
    try {
      const response = await apiFetch(`${API_URL}/api/salon/tables/${table.id}/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(openForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao abrir comanda.');
      setOpeningTable(null);
      setOpenForm({ customerName: '', peopleCount: '' });
      setSelectedSession(data.session);
      setSuccess(`Comanda aberta na mesa ${table.number}.`);
      await loadTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const openCloseDialog = (session) => {
    setCloseError('');
    const subtotal = Number(session.subtotal ?? session.totalConsumed ?? session.total ?? 0);
    const discount = Number(session.discount || 0);
    const serviceFee = roundMoney(subtotal * 0.1);
    setPaymentDraft({
      discount: discount ? String(discount.toFixed(2)) : '',
      applyServiceFee: true,
      pix: String(Math.max(0, subtotal - discount + serviceFee).toFixed(2)),
      debit: '',
      credit: '',
      cash: '',
    });
    setCloseDialog({ open: true, session });
  };

  const toggleServiceFee = (checked) => {
    setPaymentDraft((previous) => {
      const oldTotals = calculateCloseTotals(closeDialog.session, previous);
      const next = { ...previous, applyServiceFee: checked };
      const nextTotals = calculateCloseTotals(closeDialog.session, next);
      const onlyAutomaticPix = normalizeMoney(previous.debit) === 0
        && normalizeMoney(previous.credit) === 0
        && normalizeMoney(previous.cash) === 0
        && Math.abs(normalizeMoney(previous.pix) - oldTotals.totalDue) < 0.011;
      return onlyAutomaticPix ? { ...next, pix: nextTotals.totalDue.toFixed(2) } : next;
    });
  };

  const printConference = async () => {
    if (!closeDialog.session) return;
    const authSession = getTenantSettingsSession();
    if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, authSession)) return;
    setPrintingConference(true);
    setCloseError('');
    try {
      const freshSession = await fetchSession(closeDialog.session.id);
      if (!getTenantStorageKey(PRINTER_SETTINGS_KEY, authSession)) return;
      if (!freshSession) throw new Error('Não foi possível atualizar a comanda antes da impressão.');
      const freshTotals = calculateCloseTotals(freshSession, paymentDraft);
      setCloseDialog((previous) => ({ ...previous, session: freshSession }));
      await printSalonConference({
        authSession,
        session: freshSession,
        totals: freshTotals,
        includeServiceFee: paymentDraft.applyServiceFee === true,
      });
      setSuccess('Conferência enviada para a impressora do Caixa.');
    } catch (err) {
      setCloseError(err.message);
    } finally {
      setPrintingConference(false);
    }
  };

  const openPaymentReviewDialog = (session) => {
    setPaymentReviewReason('');
    setPaymentReviewError('');
    setPaymentReviewDialog({ open: true, session });
  };

  const reviewPayment = async (decision) => {
    const session = paymentReviewDialog.session;
    const request = getSalonPaymentRequest(session);
    if (!session?.id || !request?.requestId) return;
    if (decision === 'pending' && !paymentReviewReason.trim()) {
      setPaymentReviewError('Informe o que o garçom precisa corrigir.');
      return;
    }
    setPaymentReviewLoading(true);
    setPaymentReviewError('');
    try {
      const path = decision === 'confirm' ? 'confirm' : 'pending';
      const response = await apiFetch(`${API_URL}/api/salon/sessions/${session.id}/payment-review/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: request.requestId,
          revision: request.revision,
          ...(decision === 'pending' ? { reason: paymentReviewReason.trim() } : null),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao revisar o pagamento.');
      setPaymentReviewDialog({ open: false, session: null });
      setSelectedSession(decision === 'confirm' ? null : data.session);
      setSuccess(decision === 'confirm'
        ? 'Pagamento confirmado. Mesa fechada e liberada.'
        : 'Pagamento marcado como pendente para correção do garçom.');
      await loadTables();
    } catch (err) {
      setPaymentReviewError(err.message);
    } finally {
      setPaymentReviewLoading(false);
    }
  };

  const fillRemainingPayment = (method) => {
    setPaymentDraft((prev) => {
      const totals = calculateCloseTotals(closeDialog.session, prev);
      const remaining = Math.max(0, totals.remaining);
      if (!remaining) return prev;
      const current = normalizeMoney(prev[method]);
      return { ...prev, [method]: String((current + remaining).toFixed(2)) };
    });
  };

  const closeSession = async () => {
    const session = closeDialog.session;
    if (!session) return;
    setCloseError('');
    const totals = calculateCloseTotals(session, paymentDraft);
    if (totals.discount > totals.subtotal) {
      setCloseError('O desconto não pode ser maior que o subtotal.');
      return;
    }
    if (totals.totalDue < 0) {
      setCloseError('Total final inválido.');
      return;
    }
    if (totals.totalDue > 0.001 && !totals.payments.rows.length) {
      setCloseError('Informe pelo menos uma forma de pagamento.');
      return;
    }
    if (totals.nonCash > totals.totalDue + 0.001) {
      setCloseError('Pix e cartão não podem passar do total.');
      return;
    }
    if (totals.paid + 0.001 < totals.totalDue) {
      setCloseError(`Falta ${formatCurrency(totals.totalDue - totals.paid)}.`);
      return;
    }
    try {
      const response = await apiFetch(`${API_URL}/api/salon/sessions/${session.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: totals.discount,
          applyServiceFee: paymentDraft.applyServiceFee === true,
          payments: totals.payments.rows,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao fechar mesa.');
      setCloseDialog({ open: false, session: null });
      setSelectedSession(null);
      setSuccess('Mesa fechada e liberada.');
      await loadTables();
    } catch (err) {
      setCloseError(err.message);
    }
  };

  const openCancelDialog = (item) => {
    setCancelReason('');
    setCancelError('');
    setCancelDialog({ open: true, item });
  };

  const cancelItem = async () => {
    if (!selectedSession?.id || !cancelDialog.item) return;
    if (!cancelReason.trim()) {
      setCancelError('Informe o motivo do cancelamento.');
      return;
    }
    setCancelError('');
    try {
      const response = await apiFetch(`${API_URL}/api/salon/sessions/${selectedSession.id}/cancel-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: cancelDialog.item.id, reason: cancelReason }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao cancelar item.');
      setSelectedSession(data.session);
      setCancelDialog({ open: false, item: null });
      setCancelReason('');
      setSuccess('Item cancelado.');
      await loadTables();
    } catch (err) {
      setCancelError(err.message);
    }
  };

  const statusOptions = useMemo(() => ['todos', ...Object.keys(STATUS_LABELS)], []);
  const managementButton = mayManageTables ? (
    <Button
      variant="contained"
      startIcon={<SettingsIcon />}
      onClick={() => setManagementOpen(true)}
      sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 42 }}
    >
      Configuração Mesas
    </Button>
  ) : null;

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {managementButton && (managementActionContainer
        ? createPortal(managementButton, managementActionContainer)
        : <Stack direction="row" justifyContent="flex-end">{managementButton}</Stack>)}

      {!managementOpen && error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {!managementOpen && success ? <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert> : null}

      <Box
        aria-label="Mesas do salão"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: { xs: 1.25, sm: 1.75 },
        }}
      >
        {prioritizedTables.map((table) => {
          const colors = STATUS_COLORS[table.status] || STATUS_COLORS.livre;
          const awaitingCashier = isSalonPaymentAwaitingCashier(table.activeSession);
          const inactive = !table.active || table.status === 'inativa';
          const canOperate = Boolean(table.activeSession?.id) || mayOperateTables;
          const statusLabel = awaitingCashier
            ? 'Aguardando conferência no caixa'
            : STATUS_LABELS[table.status] || table.status;
          const actionLabel = table.activeSession?.id ? 'abrir detalhes' : 'abrir comanda';
          return (
            <ButtonBase
              key={table.id}
              onClick={() => handleOperationalTableClick(table)}
              disabled={inactive || !canOperate}
              aria-label={`Mesa ${table.number}, ${statusLabel}${inactive || !canOperate ? '' : `, ${actionLabel}`}`}
              title={`Mesa ${table.number} — ${statusLabel}`}
              sx={{
                width: { xs: 76, sm: 88 },
                height: { xs: 76, sm: 88 },
                flex: '0 0 auto',
                borderRadius: '50%',
                border: `${awaitingCashier ? 4 : 3}px solid ${awaitingCashier ? '#7c3aed' : colors.border}`,
                bgcolor: colors.bg,
                color: colors.color,
                animation: awaitingCashier ? `${paymentBlink} 1.15s ease-in-out infinite` : 'none',
                transition: 'transform 150ms ease, box-shadow 150ms ease',
                '&:hover': inactive || !canOperate ? undefined : {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 18px ${colors.border}33`,
                },
                '&:focus-visible': {
                  outline: '3px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 3,
                },
                '&.Mui-disabled': {
                  color: colors.color,
                  bgcolor: colors.bg,
                  opacity: inactive ? 0.68 : 0.8,
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  transition: 'none',
                  '&:hover': { transform: 'none' },
                },
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: { xs: '1.35rem', sm: '1.55rem' }, fontWeight: 900, lineHeight: 1 }}
              >
                {table.number}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>

      {selectedSession ? (
        <Dialog
          open
          onClose={() => setSelectedSession(null)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
          aria-labelledby="salon-session-dialog-title"
        >
          <DialogTitle id="salon-session-dialog-heading" sx={{ pr: 7 }}>
            <Box component="span" id="salon-session-dialog-title">
              Mesa {selectedSession?.tableNumber}
            </Box>
            <IconButton
              onClick={() => setSelectedSession(null)}
              aria-label="Fechar detalhes da mesa"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {getSalonUndeliveredItems(selectedSession).length > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>Entregue ou cancele todos os itens antes de confirmar o pagamento.</Alert>
            ) : null}
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={1.5}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Comanda #{selectedSession.id} • {STATUS_LABELS[selectedSession.status] || selectedSession.status}
                </Typography>
                {selectedSession.waiterName ? <Typography variant="body2">Garçom: {selectedSession.waiterName}</Typography> : null}
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={`Pendente: ${formatCurrency(selectedSession.pendingSubtotal)}`} />
                <Chip color="primary" label={`Total: ${formatCurrency(selectedSession.total)}`} />
                {isSalonPaymentAwaitingCashier(selectedSession) ? (
                  <Button color="secondary" variant="contained" onClick={() => openPaymentReviewDialog(selectedSession)} disabled={getSalonUndeliveredItems(selectedSession).length > 0}>
                    Confirmar pagamento do garçom
                  </Button>
                ) : null}
                {mayClose && !isSalonPaymentAwaitingCashier(selectedSession) && (
                  <Button variant="contained" startIcon={<PaymentsIcon />} onClick={() => openCloseDialog(selectedSession)} disabled={!['aberta', 'aguardando_pagamento'].includes(selectedSession.status) || getSalonUndeliveredItems(selectedSession).length > 0}>
                    Receber e fechar no caixa
                  </Button>
                )}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              {groupItemsByRound(selectedSession.items || []).map(([round, items]) => (
                <Box key={round}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>{round}</Typography>
                  <Stack spacing={0.75}>
                    {items.map((item) => (
                      <Paper key={item.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: item.status === 'cancelled' ? '#f8fafc' : '#fff' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                          <Box>
                            <Typography fontWeight={800}>{item.quantity}x {item.name}</Typography>
                            {item.options?.length ? <Typography variant="caption">+ {item.options.map((option) => option.name).join(', ')}</Typography> : null}
                            {item.observations ? <Typography variant="caption" display="block">Obs: {item.observations}</Typography> : null}
                            <Typography variant="caption" display="block" color="text.secondary">
                              Lançado por {item.waiterName || 'operador'} • {new Date(item.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                          <Stack alignItems={{ sm: 'flex-end' }}>
                            <Chip size="small" label={item.status === 'pending' ? 'Não enviado' : item.status === 'sent' ? 'Enviado' : item.status === 'ready' ? 'Pronto' : item.status === 'delivered' ? 'Entregue' : 'Cancelado'} />
                            <Typography fontWeight={900}>{formatCurrency(item.totalPrice)}</Typography>
                            {mayOperateTables && item.status !== 'cancelled' && (item.status === 'pending' || mayCancelSent) ? (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => openCancelDialog(item)}
                                sx={{ mt: 0.75 }}
                              >
                                Cancelar
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
            {selectedSession.history?.length ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Histórico</Typography>
                <Stack spacing={0.75}>
                  {selectedSession.history.slice().reverse().slice(0, 8).map((entry) => (
                    <Typography key={entry.id || `${entry.action}-${entry.createdAt}`} variant="caption" color="text.secondary">
                      {entry.createdAt ? new Date(entry.createdAt).toLocaleString('pt-BR') : ''} • {entry.message || entry.action} {entry.userName ? `• ${entry.userName}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedSession(null)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      ) : null}

      <Dialog
        open={managementOpen && mayManageTables}
        onClose={() => setManagementOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby="salon-management-dialog-title"
      >
        <DialogTitle id="salon-management-dialog-heading" sx={{ pr: 7 }}>
          <Box component="span" id="salon-management-dialog-title">
            Configuração Mesas
          </Box>
          <IconButton
            onClick={() => setManagementOpen(false)}
            aria-label="Fechar configuração de mesas"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {mayConfigureOperation && <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Typography fontWeight={800}>Abertura sem nome do cliente</Typography>
                {operationSettingsError && <Alert severity="error">{operationSettingsError}</Alert>}
                <FormControlLabel label="Gerar Cliente 1, Cliente 2... quando o nome não for informado"
                  control={<Checkbox checked={operationSettings?.autoCustomerName === true}
                    disabled={!operationSettings || operationSettingsSaving}
                    onChange={event => setOperationSettings(current => ({ ...current, autoCustomerName: event.target.checked }))} />} />
                <TextField type="time" label="Horário de fechamento da loja" value={operationSettings?.closingTime || '00:00'}
                  disabled={!operationSettings || operationSettingsSaving} InputLabelProps={{ shrink: true }}
                  helperText="A numeração reinicia neste horário (horário de Brasília)."
                  onChange={event => setOperationSettings(current => ({ ...current, closingTime: event.target.value }))} />
                <Button variant="outlined" onClick={saveOperationSettings} disabled={!operationSettings || operationSettingsSaving}>
                  {operationSettingsSaving ? 'Salvando...' : 'Salvar abertura automática'}
                </Button>
              </Stack>
            </Paper>}
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              alignItems={{ lg: 'center' }}
              justifyContent="space-between"
              gap={1.5}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ flex: 1 }}>
                <TextField
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar mesa"
                  size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                  inputProps={{ 'aria-label': 'Buscar mesa por número ou nome' }}
                  sx={{ minWidth: { sm: 220 } }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: { sm: 220 } }}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status === 'todos' ? 'Todos' : STATUS_LABELS[status]}</option>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTables} disabled={loading}>
                  Atualizar
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openTableDialog()}>
                  Criar mesa
                </Button>
              </Stack>
            </Stack>

            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
            {success ? <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert> : null}

            {managementTables.length ? (
              <Grid container spacing={1.5}>
                {managementTables.map((table) => {
                  const colors = STATUS_COLORS[table.status] || STATUS_COLORS.livre;
                  const awaitingCashier = isSalonPaymentAwaitingCashier(table.activeSession);
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={table.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderColor: awaitingCashier ? '#7c3aed' : colors.border,
                          borderWidth: awaitingCashier ? 2 : 1,
                          bgcolor: colors.bg,
                          borderRadius: 2,
                          animation: awaitingCashier ? `${paymentBlink} 1.15s ease-in-out infinite` : 'none',
                          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.25}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                              <Stack direction="row" alignItems="center" gap={1}>
                                <EventSeatIcon sx={{ color: colors.color }} />
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>
                                    Mesa {table.number}
                                  </Typography>
                                  {table.name ? <Typography variant="caption">{table.name}</Typography> : null}
                                </Box>
                              </Stack>
                              <Chip size="small" label={STATUS_LABELS[table.status] || table.status} sx={{ color: colors.color, borderColor: colors.border }} variant="outlined" />
                            </Stack>
                            {table.activeSession ? (
                              <Stack direction="row" gap={1} flexWrap="wrap">
                                <Chip size="small" label={formatCurrency(table.activeSession.total)} />
                                <Chip size="small" label={`${table.activeSession.items?.length || 0} item(ns)`} />
                                {awaitingCashier ? <Chip size="small" color="secondary" label="Conferir pagamento" /> : null}
                              </Stack>
                            ) : null}
                            <Stack direction="row" gap={1} flexWrap="wrap">
                              <Button size="small" variant="outlined" startIcon={<QrCode2Icon />} onClick={() => setQrTable(table)}>
                                QR da mesa
                              </Button>
                              <IconButton size="small" onClick={() => openTableDialog(table)} aria-label={`Editar mesa ${table.number}`}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <Button size="small" variant="outlined" color={table.active ? 'warning' : 'success'} onClick={() => toggleTableActive(table)}>
                                {table.active ? 'Inativar' : 'Ativar'}
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info">Nenhuma mesa encontrada para os filtros selecionados.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagementOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tableDialog.open} onClose={() => setTableDialog({ open: false, table: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{tableDialog.table ? 'Editar mesa' : 'Criar mesa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {tableFormError ? <Alert severity="error">{tableFormError}</Alert> : null}
            <TextField label="Número" value={tableForm.number} onChange={(event) => setTableForm((prev) => ({ ...prev, number: event.target.value }))} />
            <TextField label="Nome/apelido" value={tableForm.name} onChange={(event) => setTableForm((prev) => ({ ...prev, name: event.target.value }))} />
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Impressão automática</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Escolha de 1 a 3 pontos. Somente pontos ativos em Configurações → Impressão aparecem aqui.
              </Typography>
              {printPointsLoading ? <Alert severity="info">Carregando pontos de impressão...</Alert> : null}
              {printPointsError ? (
                <Alert severity="error" action={<Button size="small" onClick={loadPrintPoints}>Tentar novamente</Button>}>
                  {printPointsError}
                </Alert>
              ) : null}
              {!printPointsLoading && !printPointsError && !activePrintPoints.length ? (
                <Alert severity="warning">
                  Nenhum ponto ativo foi encontrado. Cadastre e ative um ponto em Configurações → Impressão antes de salvar a mesa.
                </Alert>
              ) : null}
              {activePrintPoints.length ? (
                <Stack spacing={1}>
                  {(tableForm.printPointIds || ['']).map((pointId, index) => (
                    <Stack key={`table-print-point-${index}`} direction="row" spacing={1} alignItems="center">
                      <FormControl fullWidth size="small" required>
                        <InputLabel>{`Ponto de impressão ${index + 1}`}</InputLabel>
                        <Select
                          label={`Ponto de impressão ${index + 1}`}
                          value={pointId || ''}
                          onChange={(event) => changeTablePrintPoint(index, event.target.value)}
                        >
                          <MenuItem value="" disabled><em>Selecione um ponto</em></MenuItem>
                          {activePrintPoints
                            .filter((point) => point.id === pointId || !(tableForm.printPointIds || []).includes(point.id))
                            .map((point) => (
                              <MenuItem key={point.id} value={point.id}>
                                {point.name}{point.isDefault ? ' (padrão)' : ''}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {(tableForm.printPointIds || []).length > 1 ? (
                        <IconButton
                          color="error"
                          onClick={() => removeTablePrintPoint(index)}
                          aria-label={`Remover ponto de impressão ${index + 1}`}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addTablePrintPoint}
                      disabled={
                        (tableForm.printPointIds || []).length >= 3
                        || (tableForm.printPointIds || []).some((id) => !id)
                        || (tableForm.printPointIds || []).length >= activePrintPoints.length
                      }
                    >
                      Adicionar ponto
                    </Button>
                  </Box>
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialog({ open: false, table: null })}>Cancelar</Button>
          <Button variant="contained" onClick={saveTable} disabled={printPointsLoading || Boolean(printPointsError) || !activePrintPoints.length}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <TableQrDialog open={Boolean(qrTable)} table={qrTable} onClose={() => setQrTable(null)} />

      <Dialog open={Boolean(openingTable)} onClose={() => setOpeningTable(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Abrir mesa {openingTable?.number}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField label="Cliente (opcional)" value={openForm.customerName} onChange={(event) => setOpenForm((prev) => ({ ...prev, customerName: event.target.value }))} />
            <TextField label="Pessoas" type="number" value={openForm.peopleCount} onChange={(event) => setOpenForm((prev) => ({ ...prev, peopleCount: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpeningTable(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => openSession(openingTable)}>Abrir</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={paymentReviewDialog.open}
        onClose={() => !paymentReviewLoading && setPaymentReviewDialog({ open: false, session: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferir pagamento da mesa {paymentReviewDialog.session?.tableNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {paymentReviewError ? <Alert severity="error">{paymentReviewError}</Alert> : null}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Valor informado</Typography>
                  <Typography variant="h6" fontWeight={900} color="primary">
                    {formatCurrency(getSalonPaymentRequest(paymentReviewDialog.session)?.amount
                      ?? getSalonPaymentRequest(paymentReviewDialog.session)?.totalSnapshot
                      ?? paymentReviewDialog.session?.total)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Forma de pagamento</Typography>
                  <Typography fontWeight={900}>
                    {salonPaymentMethodLabel(getSalonPaymentRequest(paymentReviewDialog.session)?.method)}
                  </Typography>
                </Stack>
                <Divider />
                {getSalonConsumptionItems(paymentReviewDialog.session).map((item) => (
                  <Stack key={item.id} direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">{item.quantity}x {item.name}</Typography>
                    <Typography variant="body2" fontWeight={800}>{formatCurrency(item.totalPrice)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
            <TextField
              label="Motivo para deixar pendente"
              value={paymentReviewReason}
              onChange={(event) => setPaymentReviewReason(event.target.value)}
              placeholder="Ex.: valor não recebido ou forma de pagamento incorreta"
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => reviewPayment('pending')} color="warning" disabled={paymentReviewLoading}>
            Deixar pendente
          </Button>
          <Button onClick={() => reviewPayment('confirm')} variant="contained" color="success" disabled={paymentReviewLoading}>
            {paymentReviewLoading ? 'Confirmando…' : 'Pagamento confirmado'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeDialog.open}
        onClose={() => { if (!printingConference) setCloseDialog({ open: false, session: null }); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Fechar mesa {closeDialog.session?.tableNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {closeError ? <Alert severity="error">{closeError}</Alert> : null}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Itens consumidos</Typography>
              <Stack spacing={0.75}>
                {getSalonConsumptionItems(closeDialog.session).map((item) => (
                  <Stack key={item.id} direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="body2">{item.quantity}x {item.name}</Typography>
                    <Typography variant="body2" fontWeight={800}>{formatCurrency(item.totalPrice)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Resumo da conta</Typography>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Subtotal</Typography>
                      <Typography fontWeight={800}>{formatCurrency(closeTotals.subtotal)}</Typography>
                    </Stack>
                    <TextField
                      label="Desconto"
                      value={paymentDraft.discount}
                      onChange={(event) => setPaymentDraft((prev) => ({ ...prev, discount: event.target.value }))}
                      disabled={!mayManageTables}
                      helperText={!mayManageTables ? 'Somente a gestão pode alterar o desconto.' : ''}
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={paymentDraft.applyServiceFee === true}
                          onChange={(event) => toggleServiceFee(event.target.checked)}
                        />
                      )}
                      label="Taxa de serviço (10%)"
                    />
                    {paymentDraft.applyServiceFee ? (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Taxa de serviço</Typography>
                        <Typography fontWeight={800}>{formatCurrency(closeTotals.serviceFee)}</Typography>
                      </Stack>
                    ) : null}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>Total final</Typography>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 900 }}>{formatCurrency(closeTotals.totalDue)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Total pago</Typography>
                      <Typography fontWeight={800}>{formatCurrency(closeTotals.paid)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color={closeTotals.remaining > 0 ? 'error.main' : 'success.main'}>Restante</Typography>
                      <Typography fontWeight={900} color={closeTotals.remaining > 0 ? 'error.main' : 'success.main'}>{formatCurrency(closeTotals.remaining)}</Typography>
                    </Stack>
                    {closeTotals.change > 0 ? (
                      <Alert severity="success" icon={false} sx={{ fontWeight: 900 }}>
                        Troco: {formatCurrency(closeTotals.change)}
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Formas de pagamento</Typography>
                    {['pix', 'debit', 'credit', 'cash'].map((method) => (
                      <Stack key={method} direction={{ xs: 'column', sm: 'row' }} gap={1}>
                        <TextField
                          fullWidth
                          label={paymentLabel[method]}
                          value={paymentDraft[method]}
                          onChange={(event) => setPaymentDraft((prev) => ({ ...prev, [method]: event.target.value }))}
                          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                          helperText={method === 'cash' ? 'Informe o valor recebido em dinheiro para calcular o troco.' : 'Pix e cartões não podem ultrapassar o total final.'}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => fillRemainingPayment(method)}
                          disabled={closeTotals.remaining <= 0}
                          sx={{ minWidth: { sm: 176 } }}
                        >
                          Pagar restante {method === 'cash' ? 'em' : 'no'} {paymentLabel[method]}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
          <Button
            sx={{ m: '0 !important', mr: { sm: 'auto !important' }, width: { xs: '100%', sm: 'auto' } }}
            startIcon={<PrintOutlinedIcon />}
            onClick={printConference}
            disabled={printingConference}
          >
            {printingConference ? 'Imprimindo…' : 'Imprimir conferência'}
          </Button>
          <Button sx={{ m: '0 !important', width: { xs: '100%', sm: 'auto' } }} onClick={() => setCloseDialog({ open: false, session: null })} disabled={printingConference}>Cancelar</Button>
          <Button sx={{ m: '0 !important', width: { xs: '100%', sm: 'auto' } }} variant="contained" onClick={closeSession} disabled={printingConference}>Confirmar pagamento</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, item: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar item</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {cancelError ? <Alert severity="error">{cancelError}</Alert> : null}
            <Typography fontWeight={800}>{cancelDialog.item?.name}</Typography>
            <TextField
              label="Motivo"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              multiline
              minRows={2}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, item: null })}>Voltar</Button>
          <Button variant="contained" color="error" onClick={cancelItem}>Confirmar cancelamento</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalonManagementPanel;
